import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import { applyTheme, readThemeMode, resolveTheme, THEME_STORAGE_KEY, validThemeMode } from '@/lib/theme/preference';
import type { ResolvedTheme, ThemeMode } from '@/lib/theme/preference';

interface ThemeContextType {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readThemeMode);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(mode));

  useLayoutEffect(() => {
    const update = () => {
      const next = resolveTheme(mode);
      applyTheme(next);
      setResolvedTheme(next);
    };
    update();
    if (mode !== 'auto' || typeof matchMedia !== 'function') return;
    const query = matchMedia('(prefers-color-scheme: dark)');
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [mode]);

  useLayoutEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        setModeState(validThemeMode(event.newValue));
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    try { localStorage.setItem(THEME_STORAGE_KEY, next); }
    catch { /* The preference still works for this session when storage is blocked. */ }
  };

  return <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
